import React, { useState, useEffect } from 'react';
import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import smsTemplateService, { SmsTemplate } from '../../services/smsTemplateService';
import { getApiError } from '../../utils/apiError';

interface SmsSendDialogProps {
  open: boolean;
  onClose: () => void;
  patientId?: number;
  meetingId?: number;
  /** Set together with context="LEAD". Leads reuse this dialog rather than
   *  getting one of their own: the screen is identical and a second copy is a
   *  second place for the preview logic to drift. */
  leadId?: number;
  patientName: string;
  patientPhone: string | null;
  context?: 'PATIENT' | 'MEETING' | 'LEAD';
  onSendSuccess?: (message: string) => void;
  onSendError?: (error: string) => void;
}

const SmsSendDialog: React.FC<SmsSendDialogProps> = ({
  open,
  onClose,
  patientId,
  meetingId,
  leadId,
  patientName,
  patientPhone,
  context = 'PATIENT',
  onSendSuccess,
  onSendError,
}) => {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  // The text actually going out. Seeded from the chosen template with its
  // variables filled in, then editable - picking a template is a starting
  // point, not a commitment.
  const [messageText, setMessageText] = useState('');
  const [edited, setEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variableMap, setVariableMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadTemplates();
      loadEntityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, context]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const allTemplates = await smsTemplateService.getTemplates();
      const contextTemplates = allTemplates.filter(
        (t) => t.context === context && t.is_active
      );
      setTemplates(contextTemplates);
      if (contextTemplates.length > 0) {
        setSelectedTemplateId(contextTemplates[0].id);
      }
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load SMS templates'));
      if (onSendError) onSendError('Failed to load SMS templates');
    } finally {
      setLoading(false);
    }
  };

  const loadEntityData = async () => {
    try {
      if (context === 'PATIENT' && patientId) {
        const { patientService } = await import('../../services/patientService');
        const { clinicService } = await import('../../services/clinicService');
        const patient = await patientService.getPatient(patientId);
        const clinic = await clinicService.getClinic(patient.clinic_id);
        const scheduleUrl = patient.uuid
          ? `${window.location.origin}/schedule/${patient.uuid}`
          : '';
        setVariableMap({
          first_name: patient.first_name,
          last_name: patient.last_name,
          phone: patient.phone ?? '',
          email: patient.email ?? '',
          id_number: patient.id_number ?? '',
          hm_id: patient.hm_id ?? '',
          status: patient.status,
          clinic_name: clinic.name,
          schedule_url: scheduleUrl,
        });
      } else if (context === 'LEAD' && leadId) {
        const { leadService } = await import('../../services/leadService');
        const lead = await leadService.getLead(leadId);
        const map: Record<string, string> = {
          first_name: lead.first_name,
          last_name: lead.last_name,
          full_name: `${lead.first_name} ${lead.last_name}`.trim(),
          phone: lead.phone ?? '',
          email: lead.email ?? '',
          id_number: lead.id_number ?? '',
          status: lead.status,
          sub_clinic: lead.sub_clinic ?? '',
        };
        // The tenant's own fields, under the same lead_field_* names the
        // backend resolves them as, so the preview matches what is sent.
        Object.entries(lead.additional_fields ?? {}).forEach(([key, value]) => {
          const sanitized = key.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
          map[`lead_field_${sanitized}`] = value == null ? '' : String(value);
        });
        setVariableMap(map);
      } else if (context === 'MEETING' && meetingId) {
        const { meetingService } = await import('../../services/meetingService');
        const { patientService } = await import('../../services/patientService');
        const { clinicService } = await import('../../services/clinicService');
        const meeting = await meetingService.getMeeting(meetingId);
        const patient = await patientService.getPatient(meeting.patient_id);
        const clinic = await clinicService.getClinic(patient.clinic_id);
        const d = new Date(meeting.scheduled_at);
        const pad = (n: number) => String(n).padStart(2, '0');
        const nameParts = meeting.patient.full_name.split(' ');
        const scheduleUrl = patient.uuid
          ? `${window.location.origin}/schedule/${patient.uuid}`
          : '';
        setVariableMap({
          patient_first_name: nameParts[0] ?? '',
          patient_last_name: nameParts.slice(1).join(' ') ?? '',
          therapist_name: meeting.therapist.full_name,
          date: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
          time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
          meeting_type_name: meeting.meeting_type?.name ?? '',
          meeting_url: meeting.yoman_url ?? '',
          team_url: meeting.team_url ?? '',
          notes: meeting.notes ?? '',
          clinic_name: clinic.name,
          schedule_url: scheduleUrl,
        });
      }
    } catch {
      // Non-critical — preview substitution is best-effort
    }
  };

  const handleSend = async () => {
    if (!selectedTemplateId || !patientPhone) return;
    if (!messageText.trim()) {
      setError('Message is empty');
      return;
    }
    // Only sent when it differs from the template's own rendering, so an
    // untouched send keeps going through the server's substitution exactly as
    // it did before.
    const override =
      selectedTemplate && messageText !== renderPreview(selectedTemplate.content)
        ? messageText
        : undefined;

    try {
      setSending(true);
      setError(null);

      if (context === 'MEETING' && meetingId) {
        const { meetingService } = await import('../../services/meetingService');
        await meetingService.sendSms(meetingId, Number(selectedTemplateId));
      } else if (context === 'PATIENT' && patientId) {
        const { patientService } = await import('../../services/patientService');
        await patientService.sendSms(patientId, Number(selectedTemplateId), override);
      } else if (context === 'LEAD' && leadId) {
        const { leadService } = await import('../../services/leadService');
        await leadService.sendSms(leadId, Number(selectedTemplateId), override);
      } else {
        throw new Error('Invalid context or missing ID');
      }

      const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
      if (onSendSuccess) {
        onSendSuccess(`SMS "${selectedTemplate?.name}" sent successfully to ${patientName}`);
      }
      setSelectedTemplateId(templates.length > 0 ? templates[0].id : '');
      onClose();
    } catch (err: any) {
      const errorMessage = getApiError(err, 'Failed to send SMS');
      setError(errorMessage);
      if (onSendError) onSendError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  // An unresolved or empty variable renders as blank, matching what the backend
  // sends — the patient must never see a raw "{first_name}".
  const renderPreview = (text: string): string =>
    text.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_match, key) =>
      variableMap[key] == null ? '' : String(variableMap[key])
    );

  // Re-seed the box whenever the template or the resolved variables change,
  // but never over something the user has typed - losing their edit because a
  // background fetch landed would be worse than a stale preview.
  useEffect(() => {
    if (edited) return;
    setMessageText(selectedTemplate ? renderPreview(selectedTemplate.content) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, selectedTemplate, variableMap, edited]);

  const subject = context === 'LEAD' ? 'Lead' : 'Patient';
  const title = `Send SMS to ${subject}: ${patientName}${patientPhone ? `  Phone: ${patientPhone}` : ''}`;

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={title}
      isLoading={sending}
      width="max-w-xl"
      titleAlign="left"
      titleClassName="text-lg font-bold text-[#366A75] leading-tight"
    >
      <div className="space-y-4">
        {!patientPhone && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
            Patient does not have a phone number
          </div>
        )}

        {error && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700 font-bold">×</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-[#366A75] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <div className="px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded">
            No active SMS templates with {context} context found
          </div>
        ) : (
          <>
            <div>
              <label className="block text-[#366A75] font-medium mb-2">SMS Template</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => {
                  setSelectedTemplateId(Number(e.target.value));
                  setEdited(false);
                }}
                disabled={sending}
                className={`${POPUP_STYLES.input} appearance-none`}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {selectedTemplate && (
              <div>
                <label className="block text-[#366A75] font-medium mb-2">
                  Message {edited && <span className="text-xs font-normal">(edited)</span>}
                </label>
                <textarea
                  value={messageText}
                  onChange={(e) => { setMessageText(e.target.value); setEdited(true); }}
                  disabled={sending}
                  rows={4}
                  className={`${POPUP_STYLES.input} resize-none`}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-[#366A75]">
                    Characters: {messageText.length}
                  </p>
                  {edited && (
                    <button
                      type="button"
                      onClick={() => setEdited(false)}
                      disabled={sending}
                      className="text-xs text-[#366A75] underline hover:no-underline"
                    >
                      Reset to template
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleSend}
            disabled={!selectedTemplateId || !patientPhone || sending || templates.length === 0 || !messageText.trim()}
            className={POPUP_STYLES.button}
          >
            {sending ? 'Sending...' : 'Send SMS'}
          </button>
          <button
            onClick={onClose}
            disabled={sending}
            className={POPUP_STYLES.button}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default SmsSendDialog;
